import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { change } from 'redux-form';

import {
  POS_MAX_AMOUNT_PAID_SIZE,
  POS_MAX_CHECK_NUMBER_LENGTH,
} from 'Common/Constants';
import { TRANSACTION_FORM_NAME } from 'Common/FormEnum';
import { TransactionEnum } from 'Common/FieldEnum';
import { getKeyboardActive } from 'Common/UI/state/selectors';
import * as POSSelectors from 'PointOfSale/selectors';
import { Formatters } from 'Common/Helpers';
import PaymentOptionsComponent from 'PointOfSale/modules/layouts/component/PaymentOptionsComponent';
import { getValue } from 'Common/Components/Keyboard/withKeyboard';

import { TransactionTypeEnum } from 'Common/Constants/Enumeration/TransactionTypeEnum';
import { showLookup } from 'PointOfSale/modules/lookup/state/actions';
import { processTransaction } from 'PointOfSale/state/actions';
import { InputMode } from 'Common/FormEnum';
import { getValueFromInputMode } from 'Common/GeneralHelper';
import { isPreferenceEnabled } from 'Common/Security/SecurityHelpers';
import { PreferencesEnum as PrefEnum } from 'Common/Security/PreferencesEnum';
import { getPreferences } from 'Common/Security/state/selectors';

class PaymentOptionsContainer extends Component {
  static checkPopupId = 'pos-check-popup';
  transformPattern = /[translate3d|(|)]/g;

  constructor(props) {
    super(props);
    this.state = {
      isOpen: false,
      amoundPaidInputFocused: false,
      checkInputFocused: false,
    };
    this.originalPopupTransform = null;
    this.amountPaidInputRef = React.createRef();
    this.checkNumberInputRef = React.createRef();
  }

  onCheckNumber = event => {
    const { change } = this.props;
    let value = getValue(event);

    value = value.substr(0, POS_MAX_CHECK_NUMBER_LENGTH);

    const keyboardInput = this.checkNumberInputRef.current.wrappedInstance;
    keyboardInput.setKeyboardValue(value);

    change(TRANSACTION_FORM_NAME, TransactionEnum.CheckNumber, value);
  };

  updateAmountPaid(value) {
    const { change } = this.props;

    change(TRANSACTION_FORM_NAME, TransactionEnum.AmountPaid, value);
  }

  onAmountBlur = () => {
    const { amountPaid } = this.props;

    const updatedAmountPaid = getValueFromInputMode(
      InputMode.Decimal,
      POS_MAX_AMOUNT_PAID_SIZE,
      amountPaid
    );
    this.updateAmountPaid(Formatters.currency(updatedAmountPaid));
    this.setState({
      amountPaidInputFocused: false,
    });
  };

  onAmountFocus = () => {
    const { amountPaid } = this.props;
    this.updateAmountPaid(amountPaid || '');
    this.setState({ amountPaidInputFocused: true });
  };

  onAmountClear = () => {
    this.props.change(TRANSACTION_FORM_NAME, TransactionEnum.AmountPaid, '');
  };

  onAmountPaid = event => {
    const value = getValueFromInputMode(
      InputMode.Decimal,
      POS_MAX_AMOUNT_PAID_SIZE,
      getValue(event)
    );
    // Because the KeyboardInput is not aware of the validation logic,
    // its value needs to be set manually to be in sync
    const keyboardInput = this.amountPaidInputRef.current.wrappedInstance;
    keyboardInput.setKeyboardValue(value);

    this.updateAmountPaid(value);
  };

  handleOpen = () => {
    this.props.showLookup(false);
    this.setState({
      isOpen: true,
    });
  };

  handleClose = () => {
    const { keyboardActive } = this.props;
    const { amountPaidInputFocused, checkInputFocused } = this.state;

    if (amountPaidInputFocused || checkInputFocused) {
      return;
    }

    if (keyboardActive) {
      const checkPopup = document.getElementById(
        PaymentOptionsContainer.checkPopupId
      );

      checkPopup.style.transform = this.originalPopupTransform;
    }

    this.setState({
      isOpen: false,
    });
  };

  onCheckInputFocus = () => this.setState({ checkInputFocused: true });

  onCheckInputBlur = () =>
    this.setState({
      checkInputFocused: false,
    });

  computeTransformStyle(transform, verticalOffset) {
    const transformValues = transform
      .replace(this.transformPattern, '')
      .split(',');
    const transformY = `${parseFloat(transformValues[1]) - verticalOffset}px`;
    transformValues[1] = transformY;
    return `translate3d(${transformValues.join(', ')})`;
  }

  onAmountTabPress = () => {
    this.checkNumberInputRef.current.wrappedInstance.focus();
  };

  handleClick = transactionType => {
    const { transaction, processTransaction } = this.props;

    this.setState({
      isOpen: false,
    });

    transaction.TransactionType = transactionType;

    processTransaction(transaction);
  };

  render() {
    const { isOpen } = this.state;
    const {
      paymentOptions,
      checkNumber,
      writeTransactionFetching,
      amountPaid,
      preferences,
      keyboardActive,
    } = this.props;

    return (
      <Fragment>
        <PaymentOptionsComponent
          disabled={writeTransactionFetching}
          isOpen={isOpen}
          onClickHandler={this.handleClick}
          paymentOptions={paymentOptions}
          onCheckNumber={this.onCheckNumber}
          onAmountPaid={this.onAmountPaid}
          handleOpen={this.handleOpen}
          handleClose={this.handleClose}
          checkNumber={checkNumber}
          amountPaid={amountPaid}
          onAmountBlur={this.onAmountBlur}
          onAmountFocus={this.onAmountFocus}
          onAmountTabPress={this.onAmountTabPress}
          onCheckInputFocus={this.onCheckInputFocus}
          onCheckInputBlur={this.onCheckInputBlur}
          checkPopupId={PaymentOptionsContainer.checkPopupId}
          forwardedRefs={[this.amountPaidInputRef, this.checkNumberInputRef]}
          creditCardEnabled={isPreferenceEnabled(
            PrefEnum.EnableCreditCard,
            preferences
          )}
          keyboardActive={keyboardActive}
        />
      </Fragment>
    );
  }
}

PaymentOptionsContainer.propTypes = {
  paymentOptions: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
    })
  ).isRequired,

  checkNumber: PropTypes.string.isRequired,
  amountPaid: PropTypes.string.isRequired,
  writeTransactionFetching: PropTypes.bool.isRequired,
  transaction: PropTypes.object.isRequired,
  keyboardActive: PropTypes.bool.isRequired,
  preferences: PropTypes.array.isRequired,
};

PaymentOptionsContainer.defaultProps = {
  paymentOptions: [
    { type: 'Credit', value: TransactionTypeEnum.Credit },
    { type: 'Check', value: TransactionTypeEnum.Check },
    { type: 'Cash', value: TransactionTypeEnum.Cash },
    { type: 'Account', value: TransactionTypeEnum.Charge },
  ],
};

const mapStateToProps = state => ({
  checkNumber: POSSelectors.getCurrentTransactionCheckNumber(state),
  amountPaid: POSSelectors.getCurrentTransactionAmountPaid(state) || '',
  writeTransactionFetching: POSSelectors.getFetching(state),
  transaction: POSSelectors.getCurrentTransaction(state),
  keyboardActive: getKeyboardActive(state),
  preferences: getPreferences(state),
});

const mapDispatchToProps = {
  processTransaction,
  showLookup,
  change,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PaymentOptionsContainer);



// WEBPACK FOOTER //
// ./src/pages/pointofsale/modules/layouts/container/PaymentOptionsContainer.js